/**
 * Template de visualisation d'avoir (Credit Note)
 * ✅ Cohérent avec InvoiceTemplateClassic - utilise les mêmes composants partagés
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from '@/stores/companyStore';
import { useTemplateColors } from '@/hooks/useTemplateColors';
import { notify } from '@/stores/notificationStore';
import { isVatExempt, formatDate } from '@/utils/vatCalculations';
import { ActionBar } from '@/components/templates/shared/ActionBar';
import { ItemsTable } from '@/components/templates/shared/ItemsTable';
import { TotalsSection } from '@/components/templates/shared/TotalsSection';
import { LegalFooter } from '@/components/templates/shared/LegalFooter';
import { UnifiedEmailModal, EmailData } from '@/components/modals/UnifiedEmailModal';
import { creditNotesAPI } from '@/utils/creditNotesAPI';
import { getAccessToken } from '@/services/tokenManager';
import { getAssetUrl } from '@/utils/api';
import { AlertCircle } from 'lucide-react';

interface CreditNoteViewTemplateProps {
  creditNoteData: any;
  onClose: () => void;
}

export const CreditNoteViewTemplate: React.FC<CreditNoteViewTemplateProps> = ({
  creditNoteData,
  onClose,
}) => {
  const { t } = useTranslation();
  const { company } = useCompanyStore();
  const { preferences: colorPreferences } = useTemplateColors(company?.id);
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  // Couleur: utiliser la couleur de la société si configurée, sinon Teal par défaut
  const defaultColor = '#0D9488'; // Teal-600 (Design System Volvo)
  const creditNoteColor = (colorPreferences?.useCompanyColors && colorPreferences?.colorPrimary)
    ? colorPreferences.colorPrimary
    : defaultColor;
  const isExempt = isVatExempt(company?.vatRegime);

  if (!creditNoteData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-6 max-w-md text-center">
          <div className="p-4 bg-red-100 rounded-2xl inline-block mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 font-semibold mb-4">{t('templates.creditNote.noData')}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-all"
          >
            {t('templates.common.back')}
          </button>
        </div>
      </div>
    );
  }

  // Parser les items - utiliser CreditNoteItem du backend
  const rawItems = creditNoteData.CreditNoteItem || [];
  const items = rawItems.map((item: any) => ({
    description: item.description || '',
    unitPrice: Number(item.unitPrice) || 0,
    price: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 0,
    total: Number(item.totalHT) || 0,
    vatRate: Number(item.taxPercent) || (isExempt ? 0 : 20),
    vatAmount: Number(item.totalTTC) - Number(item.totalHT) || 0,
  }));

  // Calculs des totaux
  const subtotal = Number(creditNoteData.totalHT) || 0;
  const tax = Number(creditNoteData.totalTVA) || 0;
  const total = Number(creditNoteData.totalTTC) || 0;

  // Ventilation TVA - uses VatBreakdownItem interface
  const vatBreakdown: Array<{ rate: number; baseHT: number; vatAmount: number }> = [];
  const vatByRate: Record<number, { baseHT: number; vatAmount: number }> = {};

  items.forEach((item: any) => {
    const rate = item.vatRate;
    if (!vatByRate[rate]) {
      vatByRate[rate] = { baseHT: 0, vatAmount: 0 };
    }
    vatByRate[rate].baseHT += item.total;
    vatByRate[rate].vatAmount += item.vatAmount;
  });

  Object.entries(vatByRate).forEach(([rate, data]) => {
    vatBreakdown.push({
      rate: Number(rate),
      baseHT: data.baseHT,
      vatAmount: data.vatAmount
    });
  });

  // Handlers
  const handleDownloadPDF = async () => {
    setIsLoading(true);
    try {
      const token = getAccessToken();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/credit-notes/${creditNoteData.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error('Erreur lors de la génération du PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avoir-${creditNoteData.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      notify.success('PDF téléchargé avec succès', 'Téléchargement');
    } catch (error) {
      notify.error('Erreur lors du téléchargement : ' + (error as Error).message, 'PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenEmailModal = async () => {
    if (!pdfBlob) {
      setIsLoading(true);
      try {
        const token = getAccessToken();
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/billing/credit-notes/${creditNoteData.id}/pdf`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error('Erreur lors de la génération du PDF');

        const blob = await response.blob();
        setPdfBlob(blob);
      } catch (error) {
        notify.error('Erreur lors du chargement du PDF : ' + (error as Error).message, 'PDF');
        setIsLoading(false);
        return;
      } finally {
        setIsLoading(false);
      }
    }
    setShowEmailModal(true);
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      await creditNotesAPI.sendEmail(creditNoteData.id, {
        recipientEmail: emailData.to,
        customSubject: emailData.subject,
        customMessage: emailData.body,
      });

      notify.success('Avoir envoyé avec succès', 'Email');
      setShowEmailModal(false);
    } catch (error) {
      notify.error('Erreur lors de l\'envoi : ' + (error as Error).message, 'Email');
      throw error;
    }
  };

  // Status config
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPLIED': return t('templates.creditNote.statusApplied');
      case 'SENT': return t('templates.creditNote.statusSent');
      default: return t('templates.creditNote.statusDraft');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPLIED': return 'bg-green-100 text-green-800';
      case 'SENT': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen print:min-h-0 print:bg-white">
      {/* Document Content */}
      <div className="py-8 print:py-0 print:mt-0">
        <div className="max-w-4xl mx-auto bg-white shadow-2xl print:shadow-none print:max-w-full" id="credit-note-template">
          <div className="p-4 md:p-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-8 md:mb-12 gap-6">
              {/* Company Info */}
              <div>
                {/* Logo si disponible */}
                {company?.logoUrl && (
                  <div className="mb-1 md:mb-2">
                    <img
                      src={getAssetUrl(company.logoUrl)}
                      alt={company.legalName || company.tradeName || 'Logo'}
                      className="h-16 md:h-20 max-w-[200px] object-contain"
                      onError={(e) => { e.currentTarget.src = '/logo.svg'; }}
                    />
                  </div>
                )}

                {/* Nom de l'entreprise */}
                {!company?.logoUrl && (
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    {company?.legalName || company?.tradeName}
                  </h2>
                )}

                <div className="text-gray-600 mt-1 md:mt-2 space-y-0.5 md:space-y-1 text-xs md:text-sm">
                  {company?.logoUrl && (
                    <p className="font-bold text-gray-800">
                      {company?.legalName || company?.tradeName}
                    </p>
                  )}
                  {company?.addressLine1 && (
                    <p>{company.addressLine1}</p>
                  )}
                  {(company?.zipCode || company?.city) && (
                    <p>
                      {company?.zipCode && `${company.zipCode} - `}
                      {company?.city}
                    </p>
                  )}
                  {company?.phone && <p>{company.phone}</p>}
                  {company?.email && (
                    <p style={{ color: creditNoteColor }}>{company.email}</p>
                  )}
                </div>
              </div>

              {/* Credit Note Number & Dates & Client */}
              <div className="md:text-right">
                {/* AVOIR Badge */}
                <div
                  className="text-white px-3 md:px-4 py-1.5 md:py-2 rounded-lg mb-3 md:mb-4 flex items-center gap-2 justify-end"
                  style={{ backgroundColor: creditNoteColor }}
                >
                  <h3 className="text-xs md:text-base font-bold">{t('templates.creditNote.title')}</h3>
                  <span className="text-xs md:text-sm font-semibold">
                    {creditNoteData.number}
                  </span>
                </div>

                {/* Status Badge */}
                <div className="mb-3 flex justify-end">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(creditNoteData.status)}`}>
                    {getStatusLabel(creditNoteData.status)}
                  </span>
                </div>

                {/* Dates */}
                <div className="text-gray-600 space-y-1 md:space-y-2 text-xs md:text-sm">
                  <div>
                    <span className="font-medium">{t('templates.creditNote.issueDateLabel')} </span>
                    <span>{formatDate(creditNoteData.issueDate)}</span>
                  </div>

                  {/* Facture source */}
                  {creditNoteData.Invoice?.number && (
                    <div>
                      <span className="font-medium">{t('templates.creditNote.sourceInvoiceLabel')} </span>
                      <span className="font-semibold">{creditNoteData.Invoice.number}</span>
                    </div>
                  )}

                  {/* Client Info */}
                  <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-300">
                    <div className="space-y-0.5 md:space-y-1">
                      {creditNoteData.Client?.fullName && (
                        <div>
                          <span className="font-medium">{t('templates.client.name')} : </span>
                          <span>{creditNoteData.Client.fullName}</span>
                        </div>
                      )}
                      {creditNoteData.Client?.companyName && (
                        <div>
                          <span className="font-medium">{t('templates.client.companyNameLabel')} : </span>
                          <span>{creditNoteData.Client.companyName}</span>
                        </div>
                      )}
                      {creditNoteData.Client?.addressLine1 && (
                        <div>
                          <span className="font-medium">{t('templates.client.address')} : </span>
                          <span>{creditNoteData.Client.addressLine1}</span>
                        </div>
                      )}
                      {creditNoteData.Client?.zipCode && (
                        <div>
                          <span className="font-medium">{t('templates.client.zipCode')} : </span>
                          <span>{creditNoteData.Client.zipCode}</span>
                        </div>
                      )}
                      {creditNoteData.Client?.city && (
                        <div>
                          <span className="font-medium">{t('templates.client.city')} : </span>
                          <span>{creditNoteData.Client.city}</span>
                        </div>
                      )}
                      {creditNoteData.Client?.email && (
                        <div>
                          <span className="font-medium">{t('templates.common.email')} : </span>
                          <span style={{ color: creditNoteColor }}>{creditNoteData.Client.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Raison de l'avoir */}
            {creditNoteData.reason && (
              <div className="mb-6 bg-amber-50 border-l-4 border-amber-600 p-3 md:p-4 rounded">
                <h3 className="text-xs md:text-sm font-semibold text-amber-900 mb-2">{t('templates.creditNote.creditNoteReason')}</h3>
                <p className="text-sm text-amber-800">{creditNoteData.reason}</p>
              </div>
            )}

            {/* Items Table */}
            <ItemsTable
              items={items}
              tableStyle="striped"
              accentColor={creditNoteColor}
              showVat={!isExempt}
            />

            {/* Totals Section */}
            <TotalsSection
              subtotal={subtotal}
              tax={tax}
              total={total}
              discount={0}
              discountType="none"
              discountValue={0}
              vatBreakdown={vatBreakdown}
              accentColor={creditNoteColor}
              showVatBreakdown={!isExempt}
            />

            {/* Notes */}
            {creditNoteData.notes && (
              <div className="mt-6 bg-gray-50 border-l-4 border-gray-400 p-3 md:p-4 rounded">
                <h3 className="text-xs md:text-sm font-semibold text-gray-700 mb-2">{t('templates.common.notes')}</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{creditNoteData.notes}</p>
              </div>
            )}

            {/* Legal Footer */}
            <LegalFooter company={company} notes={undefined} />
          </div>
        </div>
      </div>

      {/* Action Buttons - Cohérent avec factures/devis (utilise ActionBar) */}
      <ActionBar
        onGeneratePDF={handleDownloadPDF}
        onSendEmail={handleOpenEmailModal}
        onPrint={handlePrint}
        onClose={onClose}
        isLoading={isLoading}
        documentType="credit-note"
        accentColor={creditNoteColor}
      />

      {/* Email Modal */}
      {showEmailModal && (
        <UnifiedEmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSend={handleSendEmail}
          documentType="credit-note"
          documentNumber={creditNoteData.number}
          clientEmail={creditNoteData.Client?.email || ''}
          clientName={creditNoteData.Client?.companyName || creditNoteData.Client?.fullName || 'Client'}
          pdfBlob={pdfBlob || undefined}
          showPdfPreview={true}
          templateVariables={{
            creditNoteNumber: creditNoteData.number,
            clientName: creditNoteData.Client?.companyName || creditNoteData.Client?.fullName || '',
            total: total.toFixed(2),
            reason: creditNoteData.reason || '',
            invoiceNumber: creditNoteData.Invoice?.number || 'N/A',
            companyName: company?.legalName || company?.tradeName || '',
            companyEmail: company?.email || '',
            companyPhone: company?.phone || '',
          }}
        />
      )}
    </div>
  );
};

export default CreditNoteViewTemplate;
